import { useEffect, useState } from 'react';
import { Box, Btn, Select, Txt } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { REFRESH_INTERVALS } from '../hooks/useAutoRefresh';
import { DateRangePicker } from './DateRangePicker';
import { clockTime, longDate } from '../utils/format';
import { type DateRange, type ResolveContext, rangeSubLabel } from '../utils/dateRange';
import type { WarehouseInfo } from '../api/types';

/**
 * True once the page has scrolled off the top.
 *
 * Drives the header's "lifted" state. Listener is passive so it never blocks
 * scrolling, and it only writes state when the boolean actually flips.
 */
function useScrolled(threshold = 4): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const next = window.scrollY > threshold;
      setScrolled((prev) => (prev === next ? prev : next));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return scrolled;
}

/**
 * Dashboard header.
 *
 * Two tight rows with a fixed height budget:
 *   row 1 — identity (left)            | date controls (right)
 *   row 2 — the selected date, spelled out (left) | status + actions (right)
 *
 * Both rows are `items-center` and never stack on desktop, so the header cannot
 * grow a tall empty band when a control group wraps. On narrow screens the rows
 * wrap in place rather than reflowing into a column of loose controls.
 *
 * Refresh is manual and stamped — no polling, so the dashboard adds no
 * background load to the shared read-only database.
 */
export interface DashboardHeaderProps {
  warehouse: WarehouseInfo | null;
  range: DateRange;
  rangeCtx: ResolveContext;
  onRangeChange: (range: DateRange) => void;
  onRefresh: () => void;
  refreshing: boolean;
  lastUpdated: Date | null;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  user: { email: string };
  onLogout: () => void;
  /** Auto-refresh interval in ms; 0 = off. */
  refreshIntervalMs: number;
  onRefreshIntervalChange: (ms: number) => void;
  /** False when polling is paused because the tab is hidden. */
  autoRefreshActive: boolean;
}

export function DashboardHeader({
  warehouse,
  range,
  rangeCtx,
  onRangeChange,
  onRefresh,
  refreshing,
  lastUpdated,
  theme,
  onToggleTheme,
  user,
  onLogout,
  refreshIntervalMs,
  onRefreshIntervalChange,
  autoRefreshActive,
}: DashboardHeaderProps): JSX.Element {
  const label = warehouse?.label ?? '—';
  const scrolled = useScrolled();

  const location = [warehouse?.city, warehouse?.state].filter(Boolean).join(', ');
  // Manager stays discoverable as a tooltip rather than unlabelled body text.
  const identityTitle = warehouse?.manager
    ? `${label}${location ? ` — ${location}` : ''} · Manager: ${warehouse.manager}`
    : label;

  return (
    <Box
      as="header"
      /**
       * The background is fully OPAQUE, not translucent.
       *
       * A `backdrop-blur` header let the KPI cards ghost through as they
       * scrolled underneath, which read as a smear rather than depth. A solid
       * surface plus a shadow that appears only once the page has scrolled
       * gives clean separation at every scroll position.
       */
      className={[
        'sticky top-0 z-30 bg-surface-page transition-shadow duration-200',
        scrolled ? 'border-b border-hairline shadow-lift' : 'border-b border-transparent',
      ].join(' ')}
    >
      <Box className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
        {/* Row 1 — identity + date controls */}
        <Box className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-3">
          <Box className="flex items-center gap-3 min-w-0">
            <Box
              className="grid place-items-center w-9 h-9 rounded-xl shrink-0 ring-1 ring-hairline bg-surface-card"
              style={{ color: 'var(--life-transit)' }}
            >
              <Icon name="warehouse" className="w-[18px] h-[18px]" strokeWidth={1.9} />
            </Box>
            <Box className="min-w-0">
              <Box className="flex items-center gap-2">
                <Txt
                  as="h1"
                  title={identityTitle}
                  className="text-[17px] font-semibold text-ink-primary tracking-[-0.01em] truncate"
                >
                  {label} Warehouse Orders
                </Txt>
                <Txt className="hidden sm:inline px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wide text-ink-muted bg-surface-sunken ring-1 ring-hairline whitespace-nowrap">
                  Read only
                </Txt>
              </Box>
              {/*
                Location, not the `manager` column. `admin.warehouses.manager`
                is inconsistent across the estate — 13 of 318 rows hold things
                like "Demo", "Abc" or "L48 Warehouse" rather than a person — so
                showing it unlabelled beside the subtitle was confusing. The
                manager is still available on hover over the warehouse name.
              */}
              <Txt as="p" className="text-[12px] text-ink-muted truncate leading-tight">
                Order monitoring &amp; pickup tracking
                {location ? ` · ${location}` : ''}
              </Txt>
            </Box>
          </Box>

          <DateRangePicker value={range} ctx={rangeCtx} onChange={onRangeChange} />
        </Box>

        {/* Row 2 — the active range, spelled out, + actions */}
        <Box className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 pb-2.5 -mt-0.5">
          {/* rangeSubLabel already appends the day count for multi-day ranges. */}
          <Txt className="text-[12px] text-ink-secondary font-medium">
            {range.from === range.to ? longDate(range.from) : rangeSubLabel(range)}
          </Txt>

          <Box className="flex items-center gap-2">
            {/* Live indicator — a pulsing dot only while polling is actually
                running, so a paused (hidden-tab) state is never mistaken for live. */}
            {refreshIntervalMs > 0 ? (
              <Box className="flex items-center gap-1.5" title={autoRefreshActive ? 'Auto-refresh is running' : 'Paused while this tab is in the background'}>
                <Box
                  className={`w-1.5 h-1.5 rounded-full ${autoRefreshActive ? 'animate-pulse' : ''}`}
                  style={{ backgroundColor: autoRefreshActive ? 'var(--life-delivered)' : 'var(--ink-muted)' }}
                  aria-hidden
                />
                <Txt className="text-[11px] text-ink-muted hidden sm:inline">
                  {autoRefreshActive ? 'Live' : 'Paused'}
                </Txt>
              </Box>
            ) : null}

            <Select
              value={String(refreshIntervalMs)}
              onChange={(v) => onRefreshIntervalChange(Number(v))}
              aria-label="Auto-refresh interval"
              options={REFRESH_INTERVALS.map((i) => ({ value: String(i.value), label: i.label }))}
              className="h-7 px-1.5 rounded-lg bg-surface-card text-ink-secondary text-[11px] ring-1 ring-hairline hover:text-ink-primary transition-colors duration-150"
            />

            <Txt className="text-[11px] text-ink-muted tabular hidden sm:inline" aria-live="polite">
              {lastUpdated ? `Updated ${clockTime(lastUpdated)}` : 'Loading…'}
            </Txt>
            <Btn
              onPress={onRefresh}
              disabled={refreshing}
              aria-label="Refresh dashboard data"
              title="Refresh"
              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[12px] font-medium bg-surface-card text-ink-secondary ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-50"
            >
              <Icon name="refresh" className={refreshing ? 'w-3.5 h-3.5 animate-spin' : 'w-3.5 h-3.5'} />
              Refresh
            </Btn>
            <Btn
              onPress={onToggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              className="grid place-items-center w-7 h-7 rounded-lg bg-surface-card text-ink-secondary ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150"
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} className="w-3.5 h-3.5" />
            </Btn>

            {/* Signed-in identity + sign out */}
            <Box className="flex items-center gap-1.5 pl-2 ml-0.5 border-l border-hairline">
              <Txt className="text-[11px] text-ink-muted hidden md:inline max-w-[180px] truncate" title={user.email}>
                {user.email}
              </Txt>
              <Btn
                onPress={onLogout}
                aria-label="Sign out"
                title="Sign out"
                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[12px] font-medium bg-surface-card text-ink-secondary ring-1 ring-hairline hover:text-ink-primary hover:bg-surface-sunken transition-colors duration-150"
              >
                <Icon name="logout" className="w-3.5 h-3.5" />
                <Txt className="hidden sm:inline">Sign out</Txt>
              </Btn>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
